import { UserRoles } from '@/constants/UserRoles';
import { IntelligenceRoute } from '@/features/Intelligence/Router';
import { OnboardingRoutes } from '@/features/Onboarding/Router';
import { h, resolveComponent } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { installChunkReload } from './chunkReload';
import Routes from '../utils/routes';

const LocaleComponent = {
	render() {
		return h(resolveComponent('router-view'));
	},
};

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		// ===================
		// PUBLIC ROUTES
		// ===================
		{
			path: '',
			name: Routes.Home,
			component: () => import('@/views/Home/Home'),
		},
		{
			path: '/search',
			name: Routes.Search,
			component: () => import('@/views/Search/SearchView'),
		},
		{
			path: '/simulator',
			name: Routes.Simulator,
			component: () => import('@/views/Simulador/SimuladorView'),
		},
		{
			path: '/faq',
			name: Routes.FAQ,
			component: () => import('@/features/FAQ/FAQView'),
		},

		// ===================
		// MAIN MODULES
		// ===================
		{
			path: '/financial',
			component: LocaleComponent,
			meta: { roles: [UserRoles.User, UserRoles.ExternalPartner] },
			children: [
				{
					path: '',
					name: Routes.Financial,
					component: () => import('@/views/Financial/FinancialView'),
				},
			],
		},
		{
			path: '/legal',
			component: LocaleComponent,
			meta: { roles: [UserRoles.User, UserRoles.ExternalPartner] },
			children: [
				{
					path: '',
					name: Routes.Legal,
					component: () => import('@/features/Cases/Legal/LegalView'),
				},
			],
		},

		{
			path: '/cancellations',
			component: LocaleComponent,
			meta: {
				roles: [
					UserRoles.User,
					UserRoles.Admin,
					UserRoles.ExternalPartner,
					UserRoles.BrokerAdmin,
					UserRoles.BrokerUser,
					UserRoles.BrokerAgent,
				],
			},
			children: [
				{
					path: '',
					name: Routes.CancellationRequests,
					component: () => import('@/features/Cases/Cancellation/CancellationRequestsView'),
				},
			],
		},

		// Fila de pré-auditoria do contrato de locação (backoffice, sem exigir admin). Sem router
		// guard na casa: o meta documenta a matriz, a tela redireciona e a API recusa.
		{
			path: '/pre-audits',
			component: LocaleComponent,
			meta: { roles: [UserRoles.Admin, UserRoles.User] },
			children: [
				{
					path: '',
					name: Routes.PreAudits,
					component: () => import('@/features/PreAudit/PreAuditListView'),
				},
			],
		},

		// ===================
		// CASES MANAGEMENT
		// ===================
		{
			path: '/cases',
			redirect: { name: Routes.CasesList },
			name: Routes.CaseRoot,
			component: LocaleComponent,
			children: [
				// Collections Cases
				{
					path: 'collections',
					name: Routes.CollectionsRoot,
					component: LocaleComponent,
					children: [
						{
							path: '',
							name: Routes.Collections,
							component: () => import('@/features/Cases/Collections/CollectionsView'),
						},
					],
				},
				// Real Estate Cases
				{
					path: 'real-estate',
					component: LocaleComponent,
					name: Routes.RealEstateFinancingRoot,
					children: [
						{
							path: 'new',
							name: Routes.RealEstateFinancingCreate,
							component: () => import('@/features/Cases/RealEstate/CreateRealEstateFinancingView'),
						},
						{
							path: '',
							name: Routes.RealEstateFinancingCases,
							component: () => import('@/features/Cases/RealEstate/RealEstateCasesView'),
						},
					],
				},
				// Insurance Cases
				{
					path: 'insurance-guarantee-proposals',
					name: Routes.Cases,
					component: () => import('@/features/Cases/CasesView'),
					props: { caseType: 'proposals' },
				},
				{
					path: 'insurance-guarantee-contracts',
					name: Routes.Contracts,
					component: () => import('@/features/Cases/CasesView'),
					props: { caseType: 'contracts' },
				},
				// Fire Insurance Cases
				{
					path: 'fire-insurance',
					name: Routes.FireInsuranceCases,
					component: () => import('@/features/FireInsurance/FireInsuranceCasesView'),
				},
				{
					path: 'fire-insurance/new',
					name: Routes.FireInsuranceCreate,
					component: () => import('@/features/FireInsurance/FireInsuranceCreateView'),
				},
				// Surety Insurance (Seguro Fiança multicálculo)
				{
					path: 'surety-insurance',
					name: Routes.SuretyInsuranceCases,
					component: () => import('@/features/SuretyInsurance/SuretyInsuranceCasesView'),
				},
				{
					path: 'surety-insurance/new',
					name: Routes.SuretyInsuranceCreate,
					component: () => import('@/features/SuretyInsurance/SuretyInsuranceCreateView'),
				},
				// General Case Routes
				{
					path: 'new',
					name: Routes.CaseCreateView,
					component: () => import('@/features/Cases/CaseCreateView'),
				},
				{
					path: ':caseId',
					component: LocaleComponent,
					children: [
						{
							path: ':tab?',
							name: Routes.CaseView,
							component: () => import('@/features/Cases/CaseView'),
						},
					],
				},
			],
		},

		// ===================
		// PROPERTIES
		// ===================
		{
			path: '/properties',
			component: LocaleComponent,
			children: [
				{
					path: '',
					name: Routes.PropertiesView,
					component: () => import('@/views/Properties/PropertiesView'),
				},
			],
		},

		// ===================
		// MANAGEMENT SYSTEM
		// ===================
		{
			path: '/management',
			component: LocaleComponent,
			children: [
				// User Management
				{
					path: 'users',
					children: [
						{
							path: '',
							name: Routes.ManagementUsers,
							component: () => import('@/views/Management/Users/UsersView'),
						},
						{
							path: 'new',
							name: Routes.ManagementUserNew,
							component: () => import('@/views/Management/Users/UserFormView'),
						},
						{
							path: ':userId',
							name: Routes.ManagementUserView,
							component: () => import('@/views/Management/Users/UserView'),
						},
					],
				},
				// Client Search
				{
					path: 'clients',
					name: Routes.ManagementClientsSearch,
					component: () => import('@/views/Management/ClientSearch/ClientSearchView'),
				},
				// Broker Management
				{
					path: 'brokers',
					component: LocaleComponent,
					children: [
						{
							path: '',
							name: Routes.Brokers,
							component: () => import('@/views/Management/Brokers/BrokersView'),
						},
						{
							path: 'new',
							name: Routes.BrokersNew,
							component: () => import('@/views/Management/Brokers/BrokerCreateView'),
						},
						{
							path: 'ratings',
							name: Routes.ManagementBrokerRatings,
							component: () => import('@/views/Management/Brokers/BrokerRatingsView'),
						},
						{
							path: 'product-ratings',
							name: Routes.ManagementBrokerProductRatings,
							component: () => import('@/views/Management/Brokers/BrokerProductRatingsView'),
						},
						{
							path: ':brokerId',
							component: () => import('@/views/Management/Brokers/BrokerView'),
							children: [
								{
									path: '',
									name: Routes.ManagementBrokerView,
									component: () => import('@/views/Management/Brokers/BrokerDataView'),
								},
								{
									path: 'credit-policy',
									name: Routes.ManagementBrokerCreditPolicyView,
									component: () => import('@/views/Management/Brokers/BrokerCreditPolicyView'),
								},
								{
									path: 'documents',
									name: Routes.ManagementBrokerDocumentsView,
									component: () => import('@/views/Management/Brokers/BrokerDocumentsView'),
								},
								{
									path: 'products',
									name: Routes.ManagementBrokerProductsView,
									component: () => import('@/views/Management/Brokers/BrokerProductsView'),
								},
								{
									path: 'users',
									name: Routes.ManagementBrokerUsersView,
									component: () => import('@/views/Management/Brokers/BrokerUsersView'),
								},
								{
									path: 'ratings',
									name: Routes.ManagementBrokerRatingsView,
									component: () => import('@/views/Management/Brokers/BrokerRatingsTabView'),
								},
								{
									path: 'split',
									name: Routes.ManagementBrokerSplitView,
									component: () => import('@/views/Management/Brokers/BrokerSplitView'),
								},
								{
									path: 'product-ratings',
									name: Routes.ManagementBrokerProductRatingsView,
									component: () => import('@/views/Management/Brokers/BrokerProductRatingsTabView'),
								},
							],
						},
					],
				},
				// Credit Policy Management
				{
					path: 'credit-policy',
					name: Routes.ManagementCreditPolicyView,
					component: () => import('@/views/Management/CreditPolicy/CreditPolicyView'),
				},
				// Financial Management
				{
					path: 'financial',
					name: Routes.ManagementFinancialView,
					component: () => import('@/views/Management/Financial/FinancialManagementView'),
					children: [
						{
							path: 'invoices',
							name: Routes.ManagementFinancialBrokerBill,
							component: () => import('@/views/Management/Financial/BrokerFinancialManagementView'),
						},
						{
							path: 'broker-invoices',
							name: Routes.ManagementFinancialBrokerBills,
							component: () => import('@/views/Management/Financial/BrokerInvoicesManagementView'),
						},
						{
							path: 'payments-requests',
							name: Routes.ManagementFinancialPaymentRequests,
							component: () =>
								import('@/views/Management/Financial/PaymentRequestsFinancialManagementView'),
						},
						{
							path: 'setup-report',
							name: Routes.ManagementFinancialSetupReport,
							component: () => import('@/views/Management/Financial/SetupPaymentReportView'),
						},
						{
							path: 'payments',
							name: Routes.ManagementFinancialPayments,
							component: () => import('@/views/Management/Financial/PaymentsManagementView'),
						},
						{
							path: 'setup-frozen',
							name: Routes.ManagementFinancialSetupFrozen,
							component: () => import('@/views/Management/Financial/SetupFrozenQueueView'),
						},
						{
							// A mesma tela da Carteira do parceiro, no modo do ADMIN (filtro por parceiro e usuário).
							path: 'wallet',
							name: Routes.ManagementFinancialWallet,
							props: { admin: true },
							component: () => import('@/views/Financial/Broker/BrokerWalletView'),
						},
						{
							path: 'brokers-bills',
							name: Routes.ManagementFinancialBrokerBills,
							component: () => import('@/views/Management/Financial/BrokerInvoicesManagementView'),
						},
						{
							path: 'deals',
							name: Routes.ManagementDeals,
							component: () => import('@/views/Management/Financial/DealsFinancialManagementView'),
						},
					],
				},
				// Blocklist Management
				{
					path: 'griffin',
					name: Routes.GriffinView,
					component: () => import('@/views/Management/Griffin/GriffinView'),
				},
				// TJ Action Types Management
				{
					path: 'tj-action-types',
					name: Routes.ManagementTJActionTypes,
					component: () => import('@/views/Management/TJActionTypes/TJActionTypesView'),
				},
				// Managers (Empresas)
				{
					path: 'managers',
					name: Routes.ManagementManagers,
					component: () => import('@/views/Management/Managers/ManagersView'),
				},
				// Escritórios de despejo (procuração do Locador)
				{
					path: 'law-firms',
					name: Routes.ManagementLawFirms,
					component: () => import('@/views/Management/LawFirms/LawFirmsView'),
				},
				// Parameters Management
				{
					path: 'parameters',
					component: () => import('@/views/Management/Parameters/ParametersView'),
					children: [
						{
							path: '',
							name: Routes.ManagementParameters,
							redirect: { name: Routes.ManagementParametersTJ },
						},
						{
							path: 'tj',
							name: Routes.ManagementParametersTJ,
							component: () => import('@/views/Management/Parameters/TJ/TJView'),
						},
						{
							path: 'broker-rules',
							name: Routes.ManagementParametersAppBrokerRules,
							component: () => import('@/views/Management/Parameters/BrokerRules/BrokerRulesView'),
						},
					],
				},
			],
		},

		// ===================
		// FINANCIAL BROKER
		// ===================
		{
			path: '/financial/broker',
			component: () => import('@/views/Financial/Broker/BrokerFinancialView'),
			children: [
				{
					path: '',
					name: Routes.BrokerFinancial,
					component: () => import('@/views/Financial/Broker/BrokerFinancialDashboard'),
				},
				{
					path: 'bill/:year/:month',
					props: true,
					name: Routes.BrokerFinancialBills,
					component: () => import('@/views/Financial/Broker/BrokerFinancialBills'),
				},
				{
					path: 'wallet',
					name: Routes.BrokerFinancialWallet,
					component: () => import('@/views/Financial/Broker/BrokerWalletView'),
				},
			],
		},

		// ===================
		// USER ACCOUNT
		// ===================
		{
			path: '/account',
			component: () => import('@/views/Account/AccountView'),
			children: [
				{
					path: '',
					name: Routes.AccountView,
					component: () => import('@/views/Account/AccountInfos'),
				},
				{
					path: 'notifications',
					name: Routes.AccountNotifications,
					component: () => import('@/views/Account/AccountNotifications'),
				},
				{
					path: 'onboardings',
					name: Routes.AccountOnboardings,
					component: () => import('@/features/Onboarding/account/MyOnboardingsView'),
				},
				{
					path: 'security',
					name: Routes.AccountSecurity,
					component: () => import('@/views/Account/AccountSecurity'),
				},
				{
					path: 'security/change-password',
					name: Routes.AccountSecurityChangePassword,
					component: () => import('@/views/Account/AccountSecurityChangePassword'),
				},
			],
		},

		// ===================
		// NOTIFICATIONS
		// ===================
		{
			path: '/notification',
			name: Routes.NotificationsView,
			component: () => import('@/views/Notifications/NotificationsView'),
		},

		// ===================
		// COMMUNICATION
		// ===================
		{
			name: Routes.CustomerChat,
			path: '/customer-chat/:roomId?',
			component: () => import('@/features/SuperChat/SuperChatView'),
		},
		{
			path: '/messages/twilio-inbox',
			name: Routes.TwilioInbox,
			meta: { roles: [UserRoles.User, UserRoles.ExternalPartner] },
			component: () => import('@/views/messages/TwilioInboxView'),
		},

		// ===================
		// SPECIAL ROUTES
		// ===================
		{
			name: 'not-authorized',
			path: '/not-authorized',
			component: () => import('@/views/NotAuthorizedView'),
		},

		// ===================
		// ADMIN TOOLS
		// ===================
		{
			path: '/admin',
			component: LocaleComponent,
			children: [
				{
					path: 'portability-tools',
					name: Routes.PortabilityTools,
					component: () => import('@/features/PortabilityTools/PortabilityToolsView'),
				},
				{
					path: 'policy-manager',
					name: Routes.PolicyManager,
					component: () => import('@/features/PolicyManager/PolicyManagerView'),
					children: [
						{
							path: ':parameterName',
							name: Routes.PolicyManagerParameterView,
							component: () => import('@/features/PolicyManager/PolicyManagerParameterView'),
							props: true,
						},
					],
				},
				{
					path: 'products',
					name: Routes.ProductsView,
					component: () => import('@/features/Products/ProductsView'),
				},
			],
		},

		// ===================
		// REPORTS
		// ===================
		{
			path: '/reports',
			name: Routes.Reports,
			component: () => import('@/features/Reports/ReportsView'),
			redirect: { name: Routes.ReportsRenewal },
			children: [
				{
					path: 'renewal',
					name: Routes.ReportsRenewal,
					component: () => import('@/features/Renewal/RenewalsView'),
				},
				{
					path: 'payment-notifications',
					name: Routes.ReportsPaymentNotifications,
					component: () => import('@/features/PaymentNotifications/PaymentNotificationsView'),
				},
				{
					path: 'overdue-payments',
					name: Routes.ReportsOverduePayments,
					component: () => import('@/features/OverduePayments/OverduePaymentsView'),
				},
				{
					path: 'deal-auto-annulments',
					name: Routes.ReportsDealAutoAnnulments,
					component: () => import('@/features/DealAutoAnnulments/DealAutoAnnulmentsView'),
				},
				{
					path: 'deal-manual-annulments',
					name: Routes.ReportsDealManualAnnulments,
					component: () => import('@/features/DealManualAnnulments/DealManualAnnulmentsView'),
				},
				{
					path: 'fire-insurance-renewals',
					name: Routes.ReportsFireInsuranceRenewals,
					component: () => import('@/features/FireInsuranceRenewals/FireInsuranceRenewalsView'),
				},
			],
		},

		// Onboarding (material interativo + prova)
		...OnboardingRoutes,

		// Intelligence Module (External)
		IntelligenceRoute,
	],
});

installChunkReload(router);

export default router;
